import { ThemeProvider, Typography } from '@epilot/concorde-elements'
import type { ComponentType } from 'react'
import { useTranslation } from 'react-i18next'

import { useSystemDarkMode } from '../../hooks/useSystemDarkMode'
import { errorOut } from '../../services/datadog'

/**
 * What shows when the Orders page chunk cannot load (offline and not cached, or rotated away by a
 * deploy): the page's title and why it is missing, never a blank page. Part of the entry chunk, so
 * no stylesheet and nothing beyond Concorde; like the page, no app store and no App providers.
 */
/** Inline: this component ships in the entry chunk, which has no stylesheet of its own. */
const calloutStyle = {
  marginTop: 16,
  padding: '12px 16px',
  border: '1px solid color-mix(in srgb, var(--concorde-primary-color, #005eb4) 40%, transparent)',
  borderRadius: 8,
  background: 'color-mix(in srgb, var(--concorde-primary-color, #005eb4) 8%, transparent)'
} as const

export const OrdersUnavailable = () => {
  const { t } = useTranslation()
  const darkMode = useSystemDarkMode()

  return (
    <ThemeProvider darkMode={darkMode}>
      <main data-testid="offline-orders-unavailable" style={{ padding: 24 }}>
        <Typography as="h1">{t('offline.orders.title')}</Typography>
        <Typography
          as="p"
          data-testid="offline-orders-failed"
          style={calloutStyle}
          variant="secondary"
        >
          {t('offline.orders.loadFailed')}
        </Typography>
      </main>
    </ThemeProvider>
  )
}

/** A lazy Orders page import that resolves to `OrdersUnavailable` instead of rejecting. */
export const ordersPageOrUnavailable =
  <Props,>(load: () => Promise<{ default: ComponentType<Props> }>) =>
  (): Promise<{ default: ComponentType<Props> }> =>
    load().catch((originalError: unknown) => {
      const error = new Error('Offline add-on: the Orders page failed to load')

      error.cause = originalError
      errorOut(error)

      return { default: OrdersUnavailable }
    })
